import { type DocumentMeta } from "@prismicio/editor-fields"
import { useInterval } from "@prismicio/editor-support/React"
import { type AlertConfig, useAlert } from "@prismicio/editor-ui"
import { useCallback, useState } from "react"

import type { LandingPagesApiClient } from "./clients/LandingPagesApiClient"
import { GenerateLandingPagesState } from "./GenerateLandingPagesState"

export function useGenerateLandingPages(args: {
  apiClient: LandingPagesApiClient
}) {
  const { apiClient } = args
  const [taskState, setTaskState] = useState<GenerateLandingPagesState>({
    status: "NOT_STARTED",
  })
  const [executionArn, setExecutionArn] = useState<string>()

  const { showAlert } = useAlert()

  const action = useCallback(
    async (args: { document: DocumentMeta; csv: string }) => {
      const { document, csv } = args

      setTaskState({ status: "RUNNING" })

      try {
        const executionArn = await apiClient.startGenerateLandingPages({
          document,
          csv,
        })
        setExecutionArn(executionArn)
      } catch (err) {
        setTaskState({ status: "FAILED" })
        setExecutionArn(undefined)
        showGenerateLandingPagesErrorAlert({ showAlert })
      }
    },
    [showAlert, apiClient],
  )

  const checkStatus = useCallback(async () => {
    if (!executionArn) return

    const updatedTaskState = await apiClient.checkGenerateLandingPagesStatus({
      executionArn,
    })

    setTaskState(updatedTaskState)

    if (updatedTaskState.status === "RUNNING") return

    if (updatedTaskState.status !== "SUCCEEDED") {
      showGenerateLandingPagesErrorAlert({ showAlert })
    }

    setExecutionArn(undefined)
  }, [executionArn, showAlert, apiClient])

  const delay = executionArn && taskState.status === "RUNNING" ? 5000 : null

  useInterval(checkStatus, delay)

  return {
    action,
    disabled: taskState.status === "RUNNING",
    taskState,
  }
}

function showGenerateLandingPagesErrorAlert(args: {
  showAlert: (config: AlertConfig) => void
}) {
  const { showAlert } = args

  showAlert({
    title: `Oops! We were unable to generate landing pages.`,
    subtitle: `We encountered an error while trying to generate landing pages. Please contact our support team if this issue persists.`,
  })
}
